<template>
  <teleport to="body">
    <div class="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      <transition-group
        name="notification"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification"
          :class="getNotificationClasses(notification.type)"
        >
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div class="flex-shrink-0 mt-0.5">
              <div
                class="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                :class="getIconClasses(notification.type)"
              >
                {{ getIcon(notification.type) }}
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-semibold text-gray-900 mb-1">
                {{ notification.title }}
              </h4>
              <p class="text-sm text-gray-600">
                {{ notification.message }}
              </p>
            </div>

            <!-- Close button -->
            <button
              @click="removeNotification(notification.id)"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span class="sr-only">Close</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useNotificationStore } from '@/stores/notifications'
import type { Notification } from '@/stores/notifications'

const notificationStore = useNotificationStore()

const notifications = computed(() => notificationStore.notifications)

const removeNotification = (id: string) => {
  notificationStore.removeNotification(id)
}

const getNotificationClasses = (type: Notification['type']) => {
  const baseClasses = 'p-4 rounded-lg shadow-lg border bg-white'

  const typeClasses = {
    success: 'border-green-200',
    error: 'border-red-200',
    warning: 'border-yellow-200',
    info: 'border-blue-200'
  }

  return `${baseClasses} ${typeClasses[type]}`
}

const getIconClasses = (type: Notification['type']) => {
  const classes = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }

  return classes[type]
}

const getIcon = (type: Notification['type']) => {
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'i'
  }

  return icons[type]
}
</script>

<style scoped>
.notification {
  @apply transform transition-all duration-300 ease-in-out;
}

.notification-enter-from {
  @apply opacity-0 translate-x-full;
}

.notification-enter-to {
  @apply opacity-100 translate-x-0;
}

.notification-leave-from {
  @apply opacity-100 translate-x-0;
}

.notification-leave-to {
  @apply opacity-0 translate-x-full;
}

.notification-move {
  @apply transition-transform duration-300 ease-in-out;
}
</style>